import { useState } from 'react';
import { Plus, ChevronsUpDown, Check } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

/**
 * Generická vyhledávací rozbalovačka nad číselníkem (Popover + cmdk Command).
 *
 * Umožňuje vybrat položku z evidence a — pokud je předán `onCreateNew` —
 * nabízí ve footeru akci „Vytvořit nový" (přidat rovnou do číselníku, když
 * položka chybí).
 *
 * Komponenta je i18n-agnostická: všechny texty se předávají přes props,
 * aby byla znovupoužitelná napříč číselníky (firmy, přístroje, …).
 *
 * `displayLabel` slouží jako fallback pro legacy free-text hodnoty, které
 * nemají protějšek v `items` (staré objednávky bez companyId apod.) — nic
 * se tím při editaci neztratí.
 */
export default function RegistryCombobox({
  value,
  displayLabel = '',
  items = [],
  getKey,
  getLabel,
  getSublabel,
  onSelect,
  onCreateNew,
  placeholder = '— vyberte —',
  searchPlaceholder = 'Hledat…',
  emptyText = 'Nic nenalezeno',
  createLabel = 'Vytvořit nový',
  disabled = false,
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedItem = value ? items.find((it) => getKey(it) === value) : null;
  const buttonText = selectedItem ? getLabel(selectedItem) : displayLabel;

  const close = () => {
    setOpen(false);
    setSearch('');
  };

  // Enter nad hledáním, které nic nematchlo (žádná zvýrazněná položka cmdk),
  // rovnou spustí „Vytvořit nový" — to je hlavní flow, když položka v číselníku chybí.
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && onCreateNew && search.trim()) {
      const hasSelected = e.currentTarget.querySelector('[cmdk-item][aria-selected="true"]');
      if (!hasSelected) {
        e.preventDefault();
        onCreateNew(search.trim());
        close();
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          className="h-9 w-full flex items-center justify-between gap-2 rounded-md px-3 text-sm text-left disabled:opacity-50"
          style={{
            background: 'var(--ct-bg)',
            border: '1px solid var(--ct-border)',
            color: buttonText ? 'var(--ct-text)' : 'var(--ct-text-disabled)',
          }}
        >
          <span className="truncate">{buttonText || placeholder}</span>
          <ChevronsUpDown className="w-4 h-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[16rem]">
        <Command shouldFilter onKeyDown={handleKeyDown}>
          <CommandInput value={search} onValueChange={setSearch} placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {items.map((item) => {
                const key = getKey(item);
                const label = getLabel(item);
                const sub = getSublabel ? getSublabel(item) : '';
                return (
                  <CommandItem
                    key={key}
                    value={`${label} ${sub} ${key}`}
                    onSelect={() => {
                      onSelect?.(item);
                      close();
                    }}
                  >
                    <Check className={cn('w-4 h-4', value === key ? 'opacity-100' : 'opacity-0')} />
                    <span className="truncate">{label}</span>
                    {sub && (
                      <span className="ml-auto text-xs text-muted-foreground truncate">{sub}</span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          {onCreateNew && (
            <div className="border-t p-1" style={{ borderColor: 'var(--ct-border)' }}>
              <button
                type="button"
                data-testid={testid ? `${testid}-create` : undefined}
                onClick={() => {
                  onCreateNew(search.trim());
                  close();
                }}
                className="w-full flex items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-accent"
                style={{ color: 'var(--ct-accent)' }}
              >
                <Plus className="w-4 h-4" />
                <span className="truncate">
                  {search.trim() ? `${createLabel}: „${search.trim()}"` : createLabel}
                </span>
              </button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
